import { ChangeEvent } from 'react'
import { useOutletContext } from 'react-router-dom'

import { useToggle } from '@factorialco/deprecated-design-system'
import { SupplementsDataDocument } from 'generated/resources/graphql'
import type { Moment } from 'moment'

import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import type MultiFilterForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import users from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'
import TaxonomyForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/List/Line/Form/CompensationTypePopoverMenu/TaxonomyForm'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import Item from 'modules/payroll_bundle/components/Supplements/components/Item'
import TableV2 from 'modules/payroll_bundle/components/Supplements/components/Table/TableV2'
import { useExpensables } from 'modules/payroll_bundle/components/Supplements/expensablesContext'
import Pagination from 'modules/payroll_bundle/components/results/ResultsTable/Pagination'
import {
  EmployeeEstimatedPeriodBaseSalary,
  useEmployeeEstimatedPeriodBaseSalary,
} from 'modules/payroll_bundle/hooks/useEmployeeEstimatedPeriodBaseSalary'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import type { RouteContextParams } from 'modules/payroll_bundle/lib/RouteContextParams'
import { useBulkImportSupplements } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import {
  PAGINATION_PER_PAGE_SUPPLEMENTS,
  RANKS_HAS_DRAFT_SUPPLEMENTS,
  RANK_HAS_SUPPLEMENTS,
} from 'modules/payroll_bundle/lib/constants'
import { useFrequency } from 'modules/payroll_bundle/lib/frequencyContext'
import { StatusAuthorizationProvider } from 'modules/payroll_bundle/lib/statusAuthorizationsContext'
import DeleteModal from 'modules/payroll_bundle/pages/SupplementsPage/BulkImport/_components/DeleteModal'
import SupplementsInsights, {
  isSupplementFormInvalid,
} from 'modules/payroll_bundle/pages/SupplementsPage/SupplementsInsights'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

interface SupplementsProps {
  policyPeriod?: PolicyPeriod | null
  bulkImportSupplementStage?: string
  itemToHighlight?: { employeeId: number } | null
}

type SearchableSupplementsProps = SupplementsProps & {
  store: SupplementsFormStore
  loading: boolean
  currentPage: number
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  filteredEmployees: Employee[]
  search: string
  onSearch: (event: ChangeEvent<HTMLInputElement>) => void

  filteredEmployeesModels: Employee[]
  logicOperator: MultiFilterForm
  filters: MultiFilterForm[]
  onApplyFilters: (
    models: Employee[],
    defaultFilter?: MultiFilterForm[] | undefined
  ) => Promise<void>
  onCancel: () => void
  onRemoveFilter: (index: number) => void
  onAddFilter: () => void
}

const SearchableSupplements = ({
  store,
  loading,
  policyPeriod,
  itemToHighlight,
  currentPage,
  setCurrentPage,
  filteredEmployees,
  search,
  onSearch,
  filteredEmployeesModels,
  logicOperator,
  filters,
  onApplyFilters,
  onCancel,
  onRemoveFilter,
  onAddFilter,
}: SearchableSupplementsProps) => {
  const { legalEntities } = useCurrentSession().company

  const [openTaxonomyCreationModal, toggleTaxonomyCreationModal] = useToggle(false)

  const handleTaxonomyCreationModal = (newState: boolean) => {
    toggleTaxonomyCreationModal(newState)()
  }

  const { employees } = useOutletContext<RouteContextParams>()
  const { data, loading: loadingData } = useQuery(SupplementsDataDocument)
  const taxonomies = data?.contracts.taxonomies || []
  const { showDeleteModal } = useBulkImportSupplements()
  const { loading: loadingExpensables } = useExpensables()
  const { loading: loadingPayrollContextlessPolicies, canImportSupplements } =
    usePayrollContextlessPolicies()
  const canSeeSupplements = policyAuthorizations.for('payroll_supplements.supplements.read')

  const { data: estimatedEmployeesSalaries, loading: loadingEmployeesSalaries } =
    useEmployeeEstimatedPeriodBaseSalary(policyPeriod?.id)
  const estimatedEmployeesSalariesByEmployeeId: Record<number, EmployeeEstimatedPeriodBaseSalary> =
    {}
  estimatedEmployeesSalaries.forEach((salary: EmployeeEstimatedPeriodBaseSalary) => {
    estimatedEmployeesSalariesByEmployeeId[salary.employeeId] = salary
  })

  const policyPeriodStatus = policyPeriod?.attributes.status

  let isItemToHighlightRendered = false

  const totalPages = Math.ceil(filteredEmployees.length / PAGINATION_PER_PAGE_SUPPLEMENTS)
  const previousPage =
    PAGINATION_PER_PAGE_SUPPLEMENTS * currentPage - PAGINATION_PER_PAGE_SUPPLEMENTS

  const tableItems = filteredEmployees.slice(
    previousPage > 0 ? previousPage : 0,
    PAGINATION_PER_PAGE_SUPPLEMENTS * currentPage
  )

  const { date } = useFrequency()

  /**
   *  Calculate Rank of an employee using bitwise operations for a more efficient approach
   *  The function can return three possible values:
   *
   *  binary 00: Employee has no supplements.
   *  binary 01: Employee has all valid supplements.
   * binary 11: Employee has at least one draft supplement.
   */

  const calculateRank = (employee: Employee, date: Moment) => {
    let rank = 0
    const supplements = store.formsFor(employee, date)

    if (supplements.length > 0) {
      // |= bitwise OR assignment operator
      rank |= RANK_HAS_SUPPLEMENTS
      if (supplements.some((form) => !isSupplementFormInvalid(form))) {
        rank |= RANKS_HAS_DRAFT_SUPPLEMENTS
      }
    }

    return rank
  }

  const sortEmployees = (date: Moment) => {
    return (employeeA: Employee, employeeB: Employee) => {
      const employeeAFullName = users.get(employeeA.attributes.access_id)?.legalName ?? ''
      const employeeBFullName = users.get(employeeB.attributes.access_id)?.legalName ?? ''

      const rankA = calculateRank(employeeA, date)
      const rankB = calculateRank(employeeB, date)

      if (rankA !== rankB) {
        // Order by higher rank
        return rankB - rankA
      }
      // Default to name match
      return employeeAFullName.localeCompare(employeeBFullName)
    }
  }

  tableItems.sort(sortEmployees(date))

  const canChangeStatus = data?.payroll._policies.periodChangePayrollPeriodStatus.can

  return (
    <ErrorBoundary>
      <StatusAuthorizationProvider
        employees={employees}
        policyPeriod={policyPeriod}
        policyPeriodStatus={policyPeriodStatus}
      >
        {canSeeSupplements && store.forms.length > 0 && (
          <SupplementsInsights supplementsStore={store} />
        )}

        <TableV2
          supplementsStore={store}
          canChangeStatus={canChangeStatus}
          status={policyPeriodStatus}
          search={search}
          onSearch={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (currentPage !== 1) {
              setCurrentPage(1)
            }
            return onSearch(event)
          }}
          loadingEmployees={
            loading ||
            loadingExpensables ||
            loadingEmployeesSalaries ||
            loadingPayrollContextlessPolicies
          }
          tableElements={tableItems}
          estimatedEmployeesSalaries={estimatedEmployeesSalariesByEmployeeId}
          filteredEmployeesModels={filteredEmployeesModels}
          logicOperator={logicOperator}
          filters={filters}
          onApplyFilters={onApplyFilters}
          onCancel={onCancel}
          onRemoveFilter={onRemoveFilter}
          onAddFilter={onAddFilter}
          setCurrentPage={setCurrentPage}
        >
          {(props: any) => {
            // If there's an item to highlight, it forces the rendering of
            // all previous items (by scrolling them into view) in order to
            // make the item to highlight visible even if it's not in the
            // first page.
            const shouldScrollIntoView = itemToHighlight && !isItemToHighlightRendered
            if (
              itemToHighlight &&
              !isItemToHighlightRendered &&
              itemToHighlight.employeeId === props.employee.id
            ) {
              isItemToHighlightRendered = true
            }

            return (
              <Item
                {...props}
                toggleTaxonomyCreationModal={handleTaxonomyCreationModal}
                taxonomies={taxonomies}
                supplementsStore={store}
                loading={loading || loadingExpensables || loadingData}
                itemToHighlight={itemToHighlight}
                shouldScrollIntoView={shouldScrollIntoView}
              />
            )
          }}
        </TableV2>
        <Pagination
          itemsPerPage={PAGINATION_PER_PAGE_SUPPLEMENTS}
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={filteredEmployees.length}
          goToPage={(newPage) => {
            setCurrentPage(newPage)
          }}
        />
        {canImportSupplements && showDeleteModal && <DeleteModal store={store} />}
        {openTaxonomyCreationModal && (
          <TaxonomyForm legalEntities={legalEntities} setModalOpen={handleTaxonomyCreationModal} />
        )}
      </StatusAuthorizationProvider>
    </ErrorBoundary>
  )
}

export default observer(SearchableSupplements)
